import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import { useCloseGuard } from "@/hooks/use-close-guard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  caretAfterFormat,
  formatAmountInput,
  isGroupSeparator,
  parseAmountInput,
  toAmountInput,
} from "@/lib/amountInput";
import { formatCurrency, getDecimalPlaces } from "@/lib/currency";
import { proofDownloadPath } from "@/lib/paymentProof";
import type { PaymentProofDto } from "@/services/admin-payment.service";
import { PaymentProofLightbox } from "./PaymentProofLightbox";
import { ProofThumbnail } from "./ProofThumbnail";
import { AlertTriangle, Loader2, ZoomIn } from "lucide-react";
import { BusyOverlay } from "@/components/common/BusyOverlay";
import {
  useState,
  useEffect,
  useRef,
  type ChangeEvent,
  type KeyboardEvent,
} from "react";

/**
 * The subset of a payment this dialog needs. Deliberately structural rather than one of the
 * payment DTOs: the dialog is opened from the list, the detail page and the verification queue,
 * and those three carry three different shapes.
 */
export interface VerificationModalPayment {
  paymentOrderNumber: string;
  amount: number;
  currency: string;
  /** QR gateway that issued this bill, null when it was never gateway-issued. */
  qrProvider?: string | null;
  /**
   * The receipt files the customer uploaded, if the caller has them.
   *
   * Optional because one of the four call sites (provisioning a VM for a user) has no payment
   * row to read them off yet. Where they exist they belong here rather than behind the dialog:
   * the operator is being asked whether the amount matches what arrived, and the slip is the
   * only place that answer comes from.
   */
  proofs?: PaymentProofDto[];
}

interface VerificationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payment: VerificationModalPayment;
  onConfirm: (notes: string | undefined, receivedAmount: number) => void;
  isLoading: boolean;
  /**
   * Whether this dialog covers the whole page while the verify runs. A table that opens it passes
   * `false` and covers its own Card instead, so a row write does not lock the whole console.
   */
  pageOverlay?: boolean;
}

export function VerificationModal({
  open,
  onOpenChange,
  payment,
  onConfirm,
  isLoading,
  pageOverlay = true,
}: VerificationModalProps) {
  const [notes, setNotes] = useState("");
  const [receivedAmount, setReceivedAmount] = useState("");
  const amountRef = useRef<HTMLInputElement>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  /**
   * A verify request is outstanding.
   *
   * `isLoading` cannot carry this: it is the parent's mutation state and only reaches this
   * component on the NEXT render, so two clicks in the same tick both see `false` and both
   * verify the same payment. The second one loses — the payment is no longer pending — and the
   * operator is shown a failure for a verification that actually succeeded.
   */
  const confirmInFlight = useRef(false);

  // The currency's own precision, so a VND field refuses a decimal point outright while a USD
  // one still accepts cents. Hard-coding whole units here would make a $1.50 payment
  // unverifiable, since the seeded value would never match the requested figure.
  const decimals = getDecimalPlaces(payment.currency);

  const proofs = payment.proofs ?? [];
  const lightboxImages = proofs.map((proof, index) => ({
    src: proofDownloadPath(proof.downloadUrl),
    alt: `Payment Proof ${index + 1}`,
    fileName: proof.fileName,
    contentType: proof.contentType,
  }));

  useEffect(() => {
    if (!open) {
      setNotes("");
      confirmInFlight.current = false;
      setLightboxIndex(null);
    }
  }, [open]);

  // This dialog does not own the request, so it has no `finally` to release the latch in —
  // the parent's mutation settling is the signal. Also covers a parent that refuses before it
  // ever starts loading, since that leaves `isLoading` false throughout.
  useEffect(() => {
    if (!isLoading) {
      confirmInFlight.current = false;
    }
  }, [isLoading]);

  // Pre-filled with the requested figure, and grouped the way the console prints money
  // everywhere else. It stays editable: the operator overwrites it when the bank shows something
  // different, which is the case the mismatch guard below exists for.
  //
  // Keyed on the amount as well as `open` because the dialog is shared by three screens and the
  // payment behind it can be swapped while it is mounted — seeding on `open` alone would leave
  // the previous payment's figure sitting in the field. The note is deliberately NOT reset here:
  // it is the operator's own writing, and losing it because a prop moved would be a real loss.
  useEffect(() => {
    setReceivedAmount(open ? toAmountInput(payment.amount, decimals) : "");
  }, [open, payment.amount, decimals]);

  // A bill the gateway issued and never confirmed. Approving it by hand is a legitimate rescue
  // when the operator has checked the bank themselves, but it is indistinguishable afterwards
  // from waving through a bill nobody paid — so the reason is mandatory, and the server enforces
  // the same rule (GATEWAY_OVERRIDE_REASON_REQUIRED).
  const isGatewayOverride = Boolean(payment.qrProvider);

  // `parseAmountInput`, never `Number()`: the field holds the grouped form ("1,000,000"), which
  // Number() reads as NaN.
  const entered = parseAmountInput(receivedAmount);
  const isEnteredValid = entered !== null && entered > 0;
  const isMismatch = isEnteredValid && entered !== payment.amount;
  const isMissingReason = isGatewayOverride && notes.trim().length === 0;

  const canConfirm =
    isEnteredValid && !isMismatch && !isMissingReason && !isLoading;

  /**
   * Re-formats as the operator types, and puts the caret back where it was.
   *
   * Restored in a frame rather than inline: React has not written the new value to the DOM node
   * yet at this point, and setting the selection on the old string would land somewhere else. A
   * discrete event flushes its render before the next paint, so by then the field holds
   * `formatted`. Without this, correcting a digit in the middle of "1,234,567" throws the caret
   * to the end on every keystroke — worse than the plain field this replaced.
   */
  /**
   * Steps the caret over a group separator before the browser deletes anything.
   *
   * Deleting a separator on its own is a no-op — the mask re-inserts it — so an operator pressing
   * Backspace next to one watches nothing happen and presses it again. Moving the caret past it
   * first means the browser's own deletion takes the digit beyond, and one keypress always
   * removes one digit.
   *
   * Only for a collapsed caret: with text selected the browser deletes the selection, which is
   * already unambiguous.
   */
  const handleAmountKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const field = event.currentTarget;
    const start = field.selectionStart;
    if (start === null || start !== field.selectionEnd) {
      return;
    }

    if (event.key === "Backspace" && isGroupSeparator(field.value[start - 1])) {
      field.setSelectionRange(start - 1, start - 1);
    } else if (event.key === "Delete" && isGroupSeparator(field.value[start])) {
      field.setSelectionRange(start + 1, start + 1);
    }
  };

  const handleAmountInput = (event: ChangeEvent<HTMLInputElement>) => {
    const typed = event.target.value;
    const caret = event.target.selectionStart ?? typed.length;
    const formatted = formatAmountInput(typed, decimals);

    setReceivedAmount(formatted);

    const next = caretAfterFormat(typed, caret, formatted);
    requestAnimationFrame(() => {
      amountRef.current?.setSelectionRange(next, next);
    });
  };

  const handleConfirm = () => {
    if (confirmInFlight.current || !canConfirm || entered === null) return;

    confirmInFlight.current = true;
    onConfirm(notes.trim() === "" ? undefined : notes, entered);
  };

  const guard = useCloseGuard({
    open,
    onOpenChange,
    // The pre-filled amount is not the operator's own work, so on its own it does not make the
    // dialog dirty — only a note, or an amount they changed, should trigger the discard prompt.
    isDirty:
      notes.trim().length > 0 || (isEnteredValid && entered !== payment.amount),
    isSubmitting: isLoading,
    onDiscard: () => {
      setNotes("");
      setReceivedAmount(toAmountInput(payment.amount, decimals));
    },
  });

  return (
    <>
      <Dialog open={open} onOpenChange={guard.onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Verify Payment</DialogTitle>
          </DialogHeader>

          <DialogBody className="space-y-4">
            <DialogDescription>
              Approving this credits the customer's balance with the amount the
              payment was requested for. Confirm it matches what actually
              arrived.
            </DialogDescription>
            {isGatewayOverride && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>
                  {payment.qrProvider} has not confirmed this payment
                </AlertTitle>
                <AlertDescription>
                  This bill was issued by a payment gateway that never reported
                  it as paid. Verify it only if you have confirmed the money
                  arrived in the bank account yourself, and say how in the notes
                  below.
                </AlertDescription>
              </Alert>
            )}

            {/* The requested figure sits next to the field on purpose: the operator used to have
                to remember to read it off the card behind this dialog. */}
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <div className="flex items-baseline justify-between gap-4">
                <span className="text-muted-foreground">Requested</span>
                <span className="font-semibold tabular-nums">
                  {formatCurrency(payment.amount, payment.currency)}
                </span>
              </div>
              <div className="mt-1 flex items-baseline justify-between gap-4">
                <span className="text-muted-foreground">Payment</span>
                <span className="font-mono text-xs">
                  {payment.paymentOrderNumber}
                </span>
              </div>
            </div>

            {/* Above the amount field, not below it and not behind the dialog: this is the
                evidence the next question is asking about, and an operator who has to close
                this to go and look at it is an operator who stops looking. */}
            <div>
              <div className="mb-1.5 flex items-baseline justify-between gap-4">
                <Label>Transfer receipt</Label>
                {proofs.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {proofs.length} file{proofs.length > 1 ? "s" : ""} — click
                    to enlarge
                  </span>
                )}
              </div>

              {proofs.length === 0 ? (
                /* Said plainly rather than left blank. Verifying without a receipt is a real
                   decision — the API only lets a SuperAdmin do it — so the operator has to see
                   that there is nothing to check against, not just an empty space. */
                <div className="rounded-md border border-dashed px-3 py-4 text-center text-sm text-muted-foreground">
                  The customer has not uploaded a receipt for this payment.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {proofs.map((proof, index) => (
                    <button
                      type="button"
                      key={proof.attachmentId ?? proof.downloadUrl}
                      onClick={() => setLightboxIndex(index)}
                      className="group relative h-24 w-24 shrink-0 cursor-pointer overflow-hidden rounded-md border bg-muted shadow-sm transition-shadow hover:ring-2 hover:ring-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label={`Enlarge ${proof.fileName}`}
                      title={proof.fileName}
                    >
                      <ProofThumbnail proof={proof} />
                      <span className="absolute inset-0 flex items-center justify-center bg-ink/55 opacity-0 transition-opacity group-hover:opacity-100">
                        <ZoomIn className="h-5 w-5 text-white" />
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="receivedAmount">
                Amount you actually received
              </Label>
              <Input
                id="receivedAmount"
                ref={amountRef}
                // Not type="number": it refuses the thousands separator, and an input the browser
                // considers invalid reports an empty value back to onChange.
                type="text"
                inputMode={decimals > 0 ? "decimal" : "numeric"}
                autoComplete="off"
                value={receivedAmount}
                onChange={handleAmountInput}
                onKeyDown={handleAmountKeyDown}
                disabled={isLoading}
                placeholder="Enter the amount on the receipt"
                className="tabular-nums"
                aria-invalid={isMismatch}
              />
              {isMismatch && (
                <p className="mt-1 text-sm text-destructive">
                  This does not match the requested amount. Do not verify —
                  reject this payment and ask the customer to make a new deposit
                  for the amount they actually transferred.
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="notes">
                Verification Notes{" "}
                {isGatewayOverride ? "(Required)" : "(Optional)"}
              </Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={isLoading}
                placeholder={
                  isGatewayOverride
                    ? "How did you confirm the money arrived?"
                    : "Add any notes about this verification..."
                }
                rows={4}
                aria-invalid={isMissingReason}
              />
              {isMissingReason && (
                <p className="mt-1 text-sm text-destructive">
                  A reason is required when the gateway has not confirmed the
                  payment.
                </p>
              )}
            </div>
          </DialogBody>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => guard.onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={!canConfirm}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Verify Payment"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {guard.dialog}
      <BusyOverlay open={isLoading && pageOverlay} label="Verifying payment…" />

      {lightboxIndex !== null && (
        <PaymentProofLightbox
          images={lightboxImages}
          initialIndex={lightboxIndex}
          open={lightboxIndex !== null}
          onOpenChange={(next: boolean) => !next && setLightboxIndex(null)}
        />
      )}
    </>
  );
}
